<template>
  <v-container v-if="production" class="animate__animated animate__fadeInUp">
    <DetailsProductHeader 
      :create_date="create_date" 
      :production="production" 
      :is3d="true" 
      :simulation="simulation"
    />
    <v-divider class="my-2"></v-divider>
    <p class="text-caption mb-2">Results</p>
    <v-row>
      <v-col cols="12" md="6">
        <Details3DChartCard
          simulationType="2d"
          :geometries="production.GeometryList || simulation.geometries"
          title="Field on a cut through the geometries"
          caption="|Re(E)| on the plane z = constant that passes through the center of the geometries. Bright = strong field. Cyan outlines show the geometries and the star the source."
        />
      </v-col>
      <v-col cols="12" md="6">
        <Details3DChartCard
          simulationType="3d"
          :geometries="production.GeometryList || simulation.geometries"
          title="Geometries and energy flow"
          caption="Arrows show the direction of the time-averaged Poynting vector S = ½ Re(E × H*), i.e. where the energy goes. The red marker is the source; the colored solids are your geometries."
        />
      </v-col>
    </v-row>
    <p v-if="production.quality || info" class="text-caption mb-2">
      Accuracy preset: <b>{{ (info && info.quality) || production.quality }}</b>
      <template v-if="info">
        · {{ info.unknowns.toLocaleString() }} unknowns · elements of order {{ info.order }}
        · solved in {{ info.seconds }} s
      </template>
    </p>
    <v-alert v-if="info && info.order < info.requested_order" dense text type="warning" class="text-body-2">
      This result is qualitative (errors of 30–60% in the field): it was solved with first-order
      elements instead of second-order ones.
      <template v-if="info.fallback_reason === 'no_convergence'">
        The iterative solver did not converge, which happens with very high refractive indices
        <template v-if="info.max_epsilon > 16">
          — here ε = {{ info.max_epsilon }} (n = {{ Math.sqrt(info.max_epsilon).toFixed(2) }}). Real
          dielectrics go up to n ≈ 4 (ε ≈ 16); check the material (e.g. silicon is n = 3.46).
        </template>
        <template v-else>
          near a resonance. Try a slightly different size or wavelength, or the Fast preset to explore.
        </template>
      </template>
      <template v-else>
        The scene was too large for second-order elements. For accurate numbers, make it smaller:
        bring the sources closer to the geometries or reduce their size.
      </template>
    </v-alert>
    <v-row>
      <v-col cols="12" md="4">
        <p class="text-caption mb-1">Sources</p>
        <DetailsSourceCard
          v-for="(source, index) in production.SourcesList || simulation.sources"
          :key="source.token || index"
          :source="source"
          :is3d="true"
        />
      </v-col>
      <v-col cols="12" md="8">
        <p class="text-caption mb-1">Geometries</p>
        <DetailsGeometryCard :geometries="production.GeometryList || simulation.geometries" :is3d="true" />
      </v-col>
    </v-row>
  </v-container>
  <div class="containerLoading" v-else>
    <v-row justify="center" align="center" class="fill-height">
      <v-progress-circular
        :size="100"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </v-row>
  </div>
</template>
<script>
import Swal from "sweetalert2";
import DetailsSourceCard from "@/components/DetailsSourceCard";
import DetailsGeometryCard from "@/components/DetailsGeometryCard";
import DetailsProductHeader from "@/components/DetailsProductHeader";
import Details3DChartCard from "@/components/Details3DChartCard";
import simulator3d from "@/services/simulator3d";
import ngsolve from "@/services/ngsolve";
export default {
  name: "SimulationDetails",
  data: () => ({
    info: null,
    production: null,
    simulation: null,
    _id: null,
    create_date: null
  }),
  components: {
    DetailsGeometryCard,
    DetailsSourceCard,
    DetailsProductHeader,
    Details3DChartCard
  },
  methods: {
    fireError: message => {
      Swal.fire({ title: "An error occurred", text: message });
    },
    searchProductionFromSimulation: async function() {
      try {
        const { simulationID, productionID } = this.$route.params;
        const { data } = await simulator3d.get(simulationID);
        // Simulação ou produção excluída: o backend responde null
        if (!data || data.error) {
          throw new Error(data && data.message ? data.message : "Simulation not found. It may have been deleted.");
        }
        this.simulation = data;

        this.production = data.productions.find(
          production => production.content == productionID);
        if (!this.production) throw new Error("Production not found. It may have been deleted.");

        this._id = await this.production._id;
        // Resumo gravado pelo NGSolve (ordem usada, incógnitas); simulações antigas não têm
        ngsolve.get(`/simulate/${simulationID}/${productionID}/info`)
          .then(({ data: info }) => { this.info = info; })
          .catch(() => {});
        this.create_date = await this.production.create_date;

        await this.setPageInfo();
      } catch (err) {
        this.fireError(err.message);
        this.$router.push("/dashboard").catch(() => {});
      }
    },
    setPageInfo: function() {
      document.title = this.simulation.title + " - ElectrosFI";
      document
        .querySelector('meta[name="description"')
        ?.setAttribute("content", this.simulation.title.description);
    }
  },
  mounted() {
    
    this.searchProductionFromSimulation();

  }
};
</script>
